import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import NativeSelect from "@mui/material/NativeSelect";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { PlatformPage } from "@/components/PlatformPage";
import { StatusBadge } from "@/components/StatusBadge";
import { listDeletionRequests } from "../api";
import { MessagePurgeAction } from "./MessagePurgeAction";

const PAGE_SIZE = 20;

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
});

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return dateFormatter.format(date);
}

export function CompliancePage() {
  const [statusFilter, setStatusFilter] = useState("");
  const [offset, setOffset] = useState(0);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["platform", "compliance", "deletions", statusFilter, offset],
    queryFn: () =>
      listDeletionRequests({
        status: statusFilter || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  const controls = (
    <FormControl variant="standard" sx={{ minWidth: 224 }}>
      <InputLabel htmlFor="compliance-status-filter">Status filter</InputLabel>
      <NativeSelect
        value={statusFilter}
        onChange={(event) => {
          setStatusFilter(event.target.value);
          setOffset(0);
        }}
        inputProps={{ id: "compliance-status-filter" }}
      >
        <option value="">All statuses</option>
        <option value="pending">Pending</option>
        <option value="executed">Executed</option>
        <option value="cancelled">Cancelled</option>
      </NativeSelect>
    </FormControl>
  );

  return (
    <PlatformPage
      title="Compliance"
      description="Review CCPA deletion activity and execute an approved, exact-ID message-content purge."
      actions={controls}
    >
      <Stack spacing={3}>
        <MessagePurgeAction />

        <Stack
          component="section"
          spacing={2}
          aria-labelledby="deletion-activity-title"
        >
          <Typography component="h2" variant="h6" id="deletion-activity-title">
            Deletion activity
          </Typography>

          {isError ? (
            <Alert severity="error" role="alert">
              Failed to load deletion requests. Try again shortly.
            </Alert>
          ) : isLoading ? (
            <Box
              role="status"
              aria-label="Loading deletion requests"
              sx={{ display: "flex", justifyContent: "center", py: 6 }}
            >
              <CircularProgress size={36} />
            </Box>
          ) : items.length === 0 ? (
            <Typography
              role="status"
              color="text.secondary"
              sx={{ py: 4, textAlign: "center" }}
            >
              No deletion requests
            </Typography>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" variant="overline">
                      Deletion requests
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                      {total.toLocaleString("en-US")} requests
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              <TableContainer component={Card} variant="outlined">
                <Table size="small" sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell scope="col">User ID</TableCell>
                      <TableCell scope="col">Status</TableCell>
                      <TableCell scope="col">Scheduled</TableCell>
                      <TableCell scope="col">Requested</TableCell>
                      <TableCell scope="col">Resolved</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((request) => (
                      <TableRow key={request.deletion_id} hover>
                        <TableCell>
                          <Box
                            component="span"
                            title={request.user_id ?? "Erased account"}
                            sx={{ fontFamily: "monospace" }}
                          >
                            {request.user_id ?? "Erased account"}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={request.status} />
                        </TableCell>
                        <TableCell>
                          {formatTimestamp(request.scheduled_for)}
                        </TableCell>
                        <TableCell>
                          {formatTimestamp(request.requested_at)}
                        </TableCell>
                        <TableCell>
                          {formatTimestamp(
                            request.executed_at ?? request.cancelled_at,
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {total > PAGE_SIZE && (
                <Card variant="outlined">
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 2,
                    }}
                  >
                    <Button
                      type="button"
                      variant="outlined"
                      disabled={offset === 0}
                      onClick={() =>
                        setOffset((current) => Math.max(0, current - PAGE_SIZE))
                      }
                    >
                      Previous
                    </Button>
                    <Typography color="text.secondary" variant="body2">
                      Page {page} of {pageCount}
                    </Typography>
                    <Button
                      type="button"
                      variant="outlined"
                      disabled={offset + PAGE_SIZE >= total}
                      onClick={() =>
                        setOffset((current) => current + PAGE_SIZE)
                      }
                    >
                      Next
                    </Button>
                  </Stack>
                </Card>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </PlatformPage>
  );
}
